/* eslint-disable no-param-reassign */
/* eslint-disable func-names */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-underscore-dangle */
import "dayjs/locale/th";

import type { ConfigType, Dayjs } from "dayjs";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import calendar from "dayjs/plugin/calendar";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from "dayjs/plugin/duration";
import isBetween from "dayjs/plugin/isBetween";
import isoWeek from "dayjs/plugin/isoWeek";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localeData from "dayjs/plugin/localeData";
import minMax from "dayjs/plugin/minMax";
import objectSupport from "dayjs/plugin/objectSupport";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import toObject from "dayjs/plugin/toObject";
import updateLocale from "dayjs/plugin/updateLocale";
import utc from "dayjs/plugin/utc";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";

import { checkExistValueAll } from "@/utils/common/validateHelper/validateUtils";

// === Extend Plugins ===
dayjs.extend(utc);
dayjs.extend(isoWeek);
dayjs.extend(timezone);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(advancedFormat);
dayjs.extend(duration);
dayjs.extend(toObject);
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.extend(localeData);
dayjs.extend(objectSupport);
dayjs.extend(calendar);
dayjs.extend(minMax);
dayjs.extend(updateLocale);
dayjs.extend(isSameOrBefore);

// === Set Timezone ===
const BANGKOK_TIMEZONE = "Asia/Bangkok";
dayjs.tz.setDefault(BANGKOK_TIMEZONE);

// === Override Global Format ===
dayjs.extend((_, dayjsClass) => {
  const oldFormat = dayjsClass.prototype.format;
  dayjsClass.prototype.format = function (formatString?: string) {
    return oldFormat.bind(this)(formatString ?? "YYYY-MM-DD");
  };
});

// === Constants ===
const DEFAULT_DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";

// === Helpers ===
export const normalizeToFullDateTime = (input: string): string => {
  const sanitizedInput = input.trim().replace("T", " ");
  const [datePart, timePartRaw] = sanitizedInput.split(" ");
  const timePart = timePartRaw ?? "00:00:00";
  const [hh = "00", mm = "00", ss = "00"] = timePart.split(":");

  return `${datePart} ${hh.padStart(2, "0")}:${mm.padStart(2, "0")}:${ss.padStart(2, "0")}`;
};

// === Internal custom function ===
const _dayJsCustom = (
  input?: ConfigType,
  format?: string,
  strict?: boolean,
): Dayjs => {
  if (!input) {
    return dayjs().tz(BANGKOK_TIMEZONE);
  }

  if (typeof input === "string") {
    const normalized = normalizeToFullDateTime(input);
    return dayjs(normalized, DEFAULT_DATE_FORMAT, true).tz(
      BANGKOK_TIMEZONE,
      true,
    );
  }

  if (input instanceof Date) {
    return dayjs(input).tz(BANGKOK_TIMEZONE, true);
  }

  return dayjs(input).tz(BANGKOK_TIMEZONE);
};

// === Merge dayjs static methods with the wrapper function ===
type DayJsCustomType = typeof dayjs &
  ((input?: ConfigType, format?: string, strict?: boolean) => Dayjs);

const dayJsCustom = Object.assign(_dayJsCustom, dayjs) as DayJsCustomType;

// === Utility: Safe convert to Dayjs ===
export const toDayjs = (date: number | Date | string | Dayjs): Dayjs => {
  return dayJsCustom.isDayjs(date) ? date : dayJsCustom(date);
};

export const toDate = (date: Dayjs | undefined | null): Date | undefined => {
  if (!checkExistValueAll(date)) return undefined;

  return new Date(date.format("YYYY-MM-DDTHH:mm:ss"));
};

// === Exports ===
export { DEFAULT_DATE_FORMAT };
export default dayJsCustom;
